import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  FileText,
  Users,
  ShieldCheck,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Bell,
  Clock,
  Activity,
  Download,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useComplianceDashboard,
  useComplianceSummary,
  useRefreshAssessment,
  usePendingProposal,
  useResolveAlert,
  useDownloadGapAnalysisPDF,
  useDownloadEvidencePackPDF,
} from '@/hooks/use-compliance-dashboard';
import { ProposalReviewPanel } from './components/proposal-review-panel';
import { AcceptAllUndoBanner } from './components/accept-all-undo-banner';
import { OpenNonConformancesCard } from './components/open-non-conformances-card';
import { StatusBadge } from '@/app/pages/compliance/components/compliance-status';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import type {
  ComplianceClauseStatus,
  ComplianceAlert,
  ComplianceSectionRollup,
  AlertSeverity,
} from '@/lib/types';

// ─── Status helpers ─────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<AlertSeverity, { color: string; bg: string }> = {
  critical: { color: 'text-red-800', bg: 'bg-red-100' },
  warning: { color: 'text-amber-800', bg: 'bg-amber-100' },
  info: { color: 'text-blue-800', bg: 'bg-blue-100' },
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
}

function scoreTrackColor(score: number): string {
  if (score >= 80) return 'stroke-green-500';
  if (score >= 50) return 'stroke-amber-500';
  return 'stroke-red-500';
}

// ─── Module nav links ───────────────────────────────────────────────────────

const MODULE_LINKS = [
  { label: 'Personnel', to: '/compliance/personnel', icon: Users },
  { label: 'Insurance', to: '/compliance/insurance', icon: ShieldCheck },
  { label: 'Statistics', to: '/compliance/statistics', icon: BarChart3 },
  { label: 'KPIs', to: '/compliance/kpis', icon: Activity },
  { label: 'Incidents', to: '/compliance/incidents', icon: AlertTriangle },
  { label: 'Alerts', to: '/compliance/alerts', icon: Bell },
];

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export function ComplianceDashboardPage() {
  const { data, isLoading } = useComplianceDashboard();
  const { data: summaryData, isLoading: summaryLoading } = useComplianceSummary();
  const refreshMutation = useRefreshAssessment();
  const { data: pendingProposal } = usePendingProposal();
  const resolveMutation = useResolveAlert();
  const { download: downloadGapPDF } = useDownloadGapAnalysisPDF();
  const [gapPdfLoading, setGapPdfLoading] = useState(false);

  if (isLoading || summaryLoading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="col-span-2 h-56" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldCheck className="h-16 w-16 text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-gray-700">No compliance data</h2>
        <p className="mt-2 text-sm text-gray-500">
          Run an assessment to populate your compliance dashboard.
        </p>
        <Button
          className="mt-6"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Run Assessment
        </Button>
      </div>
    );
  }

  const { status_counts, clauses, alerts, deadlines, quick_stats } = data;
  const score = summaryData?.score ?? 0;
  const sectionRollups = summaryData?.section_rollups ?? [];
  const openNcCount = data.open_nc_count;
  const openNonConformances = data.open_non_conformances;

  // Full-catalog measurement breakout (Wave 6 F4): the denominator is the whole
  // clause catalog, so show how much of it has actually been assessed rather
  // than a bare percentage whose denominator the user cannot see.
  const assessed = summaryData?.assessed ?? data.assessed;
  const applicable = summaryData?.applicable ?? data.applicable;
  const catalogTotal = summaryData?.catalog_total ?? data.catalog_total;
  const unprovisioned = summaryData?.unprovisioned ?? data.unprovisioned;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Compliance Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            QP-300 compliance status — single pane of glass
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={async () => {
              setGapPdfLoading(true);
              try { await downloadGapPDF(); } finally { setGapPdfLoading(false); }
            }}
            disabled={gapPdfLoading}
            variant="outline"
          >
            <Download className={`mr-2 h-4 w-4 ${gapPdfLoading ? 'animate-pulse' : ''}`} />
            {gapPdfLoading ? 'Generating...' : 'Gap Analysis PDF'}
          </Button>
          <Button
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            variant="outline"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Assessment'}
          </Button>
        </div>
      </div>

      {/* Score ring + status counts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Score Ring */}
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <ScoreRing score={score} />
            <p className="mt-4 text-sm font-medium text-gray-500">Overall Compliance</p>
            {typeof assessed === 'number' && typeof applicable === 'number' && (
              <p
                className="mt-1 text-center text-xs text-gray-500"
                data-testid="score-coverage"
              >
                {assessed} of {applicable} applicable clauses assessed
                {typeof catalogTotal === 'number' && catalogTotal > applicable && (
                  <> · {catalogTotal} in catalog</>
                )}
              </p>
            )}
            {typeof unprovisioned === 'number' && unprovisioned > 0 && (
              <p
                className="mt-1 text-center text-xs font-medium text-amber-700"
                data-testid="score-unprovisioned"
              >
                {unprovisioned} catalog clause{unprovisioned === 1 ? '' : 's'} not yet
                provisioned and excluded from this score
              </p>
            )}
            {(summaryData?.unevidenced_compliant ?? 0) > 0 && (
              <p
                className="mt-1 text-center text-xs font-medium text-amber-700"
                data-testid="score-unevidenced-compliant"
              >
                {summaryData?.unevidenced_compliant} clause
                {summaryData?.unevidenced_compliant === 1 ? '' : 's'} marked compliant with no
                evidence on file
              </p>
            )}
          </CardContent>
        </Card>

        {/* Status breakdown + Quick Stats */}
        <div className="col-span-1 space-y-4 lg:col-span-2">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <StatusCountCard label="Compliant" count={status_counts.compliant} variant="green" />
            <StatusCountCard label="Partial" count={status_counts.partially_compliant} variant="amber" />
            <StatusCountCard label="Non-Compliant" count={status_counts.non_compliant} variant="red" />
            <StatusCountCard label="Manual Review" count={status_counts.manual_review_required ?? 0} variant="violet" />
            <StatusCountCard label="Not Assessed" count={Math.max(0, status_counts.not_assessed - (status_counts.manual_review_required ?? 0))} variant="gray" />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <QuickStatCard
              icon={FileText}
              label="Documents"
              value={String(quick_stats.documents_controlled)}
            />
            <QuickStatCard
              icon={AlertTriangle}
              label="Open Incidents"
              value={String(quick_stats.incidents_open)}
              highlight={quick_stats.incidents_open > 0}
            />
            <QuickStatCard
              icon={Activity}
              label="Latest LTIFR"
              value={quick_stats.latest_ltifr !== null ? quick_stats.latest_ltifr.toFixed(2) : '—'}
            />
            <QuickStatCard
              icon={BarChart3}
              label="Quarter Stats"
              value={formatStatsStatus(quick_stats.quarter_stats_status)}
            />
          </div>
        </div>
      </div>

      {/* Module Navigation */}
      <div className="flex flex-wrap gap-2">
        {MODULE_LINKS.map((link) => (
          <Button key={link.to} variant="outline" size="sm" asChild>
            <Link to={link.to}>
              <link.icon className="mr-1.5 h-4 w-4" />
              {link.label}
            </Link>
          </Button>
        ))}
      </div>

      {/* Assessment proposal review (S04) */}
      {pendingProposal && (
        <ProposalReviewPanel proposal={pendingProposal} />
      )}
      <AcceptAllUndoBanner />

      {/* Open non-conformances (reconciled with the assessment engine) */}
      <OpenNonConformancesCard
        count={openNcCount}
        items={openNonConformances}
      />

      {/* Section Rollup */}
      {sectionRollups.length > 0 && (
        <SectionRollupCard rollups={sectionRollups} />
      )}

      {/* Clause Matrix */}
      <ClauseMatrix clauses={clauses} />

      {/* Alerts + Deadlines side by side */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Active Alerts */}
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Active Alerts</h2>
              <Button variant="outline" size="sm" asChild>
                <Link to="/compliance/alerts">View All</Link>
              </Button>
            </div>
            {alerts.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No active alerts</p>
            ) : (
              <div className="space-y-3">
                {alerts.slice(0, 5).map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onResolve={() => resolveMutation.mutate(alert.id)}
                    isResolving={resolveMutation.isPending}
                  />
                ))}
                {alerts.length > 5 && (
                  <p className="text-center text-xs text-gray-400">
                    +{alerts.length - 5} more alerts
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card>
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
            {deadlines.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No upcoming deadlines</p>
            ) : (
              <div className="space-y-3">
                {deadlines.map((d, idx) => (
                  <div key={idx} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                    <Calendar className="h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{d.label}</p>
                      <p className="text-xs text-gray-500">{formatDateOnly(d.date)}</p>
                    </div>
                    <DaysUntilBadge date={d.date} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function ScoreRing({ score }: { score: number }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative h-36 w-36">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60" cy="60" r={radius}
          fill="none" strokeWidth="10"
          className="stroke-gray-200"
        />
        <circle
          cx="60" cy="60" r={radius}
          fill="none" strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-700 ${scoreTrackColor(score)}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${scoreColor(score)}`}>{score}%</span>
      </div>
    </div>
  );
}

function StatusCountCard({ label, count, variant }: { label: string; count: number; variant: 'green' | 'amber' | 'red' | 'gray' | 'violet' }) {
  const colors: Record<string, string> = {
    green: 'border-green-200 bg-green-50 text-green-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-500',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
  };
  return (
    <div className={`rounded-lg border p-4 text-center ${colors[variant]}`}>
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs font-medium">{label}</p>
    </div>
  );
}

function QuickStatCard({
  icon: Icon,
  label,
  value,
  highlight = false,
}: {
  icon: typeof FileText;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-amber-200 bg-amber-50' : ''}>
      <CardContent className="flex items-center gap-3 py-3">
        <Icon className="h-5 w-5 shrink-0 text-gray-400" />
        <div className="min-w-0">
          <p className="text-lg font-bold text-gray-900">{value}</p>
          <p className="truncate text-xs text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ClauseMatrix({ clauses }: { clauses: ComplianceClauseStatus[] }) {
  const timeZone = useOrgTimeZone();
  const [expanded, setExpanded] = useState<string | null>(null);
  const { download: downloadEvidencePack } = useDownloadEvidencePackPDF();
  const [epLoading, setEpLoading] = useState<number | null>(null);

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">QP-300 Clause Matrix</h2>
        <div className="space-y-1">
          {clauses.map((clause) => {
            const isOpen = expanded === clause.clause_ref;

            return (
              <div key={clause.clause_ref}>
                <button
                  onClick={() => setExpanded(isOpen ? null : clause.clause_ref)}
                  className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors hover:bg-gray-50"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                  )}
                  <StatusBadge status={clause.status} />
                  <span className="text-sm font-medium text-gray-700">
                    {clause.clause_ref}
                  </span>
                  <span className="truncate text-sm text-gray-500">
                    {clause.clause_title}
                  </span>
                </button>
                {isOpen && (
                  <div className="ml-11 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm">
                    {clause.evidence_notes ? (
                      <p className="text-gray-700">{clause.evidence_notes}</p>
                    ) : (
                      <p className="italic text-gray-400">No evidence notes available</p>
                    )}
                    {clause.assessed_at && (
                      <p className="mt-2 text-xs text-gray-400">
                        Last assessed: {formatDateTimeInTimeZone(clause.assessed_at, timeZone, { dateStyle: 'medium' })}
                      </p>
                    )}
                    {clause.next_review_due && (
                      <p className="text-xs text-gray-400">
                        Next review: {formatDateOnly(clause.next_review_due)}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      disabled={epLoading === clause.id}
                      onClick={async () => {
                        setEpLoading(clause.id);
                        try { await downloadEvidencePack(clause.id); } finally { setEpLoading(null); }
                      }}
                    >
                      <Download className={`mr-1.5 h-3.5 w-3.5 ${epLoading === clause.id ? 'animate-pulse' : ''}`} />
                      {epLoading === clause.id ? 'Generating...' : 'Evidence Pack PDF'}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {clauses.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">
              No clause data. Run an assessment to populate.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function sectionBarColor(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function SectionRollupCard({ rollups }: { rollups: ComplianceSectionRollup[] }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Section Compliance</h2>
        <div className="space-y-3">
          {rollups.map((rollup) => {
            const name = rollup.section_name || `Section ${rollup.section}`;
            return (
              <div
                key={rollup.section}
                className="flex items-center gap-4 rounded-lg px-2 py-2"
              >
                <span className="w-48 shrink-0 text-sm font-medium text-gray-700">
                  {rollup.section}. {name}
                </span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${sectionBarColor(rollup.percent)}`}
                    style={{ width: `${rollup.percent}%` }}
                  />
                </div>
                <span className="w-20 shrink-0 text-right text-sm font-medium text-gray-600">
                  {rollup.compliant}/{rollup.total} ({rollup.percent}%)
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertRow({
  alert,
  onResolve,
  isResolving,
}: {
  alert: ComplianceAlert;
  onResolve: () => void;
  isResolving: boolean;
}) {
  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 px-4 py-3">
      <Bell className={`mt-0.5 h-4 w-4 shrink-0 ${sev.color}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge className={`${sev.bg} ${sev.color}`}>{alert.severity}</Badge>
          <span className="truncate text-sm font-medium text-gray-900">{alert.title}</span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">{alert.description}</p>
        {alert.due_date && (
          <p className="mt-1 text-xs text-gray-400">
            <Clock className="mr-1 inline h-3 w-3" />
            Due: {formatDateOnly(alert.due_date)}
          </p>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onResolve}
        disabled={isResolving}
        className="shrink-0"
      >
        Resolve
      </Button>
    </div>
  );
}

function DaysUntilBadge({ date }: { date: string }) {
  const orgToday = useOrgToday();
  const days = differenceInCalendarDays(parseISO(date), parseISO(orgToday));

  if (days < 0) return <Badge className="bg-red-100 text-red-800">Overdue</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-800">{days}d</Badge>;
  return <Badge className="bg-green-100 text-green-800">{days}d</Badge>;
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function formatStatsStatus(s: string): string {
  const map: Record<string, string> = {
    submitted: 'Submitted',
    draft: 'Draft',
    not_created: 'Missing',
    approved: 'Approved',
  };
  return map[s] || s;
}
